import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Paper,
  Chip,
  Button,
  IconButton,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import CloseIcon from "@mui/icons-material/Close";
import apiService from "../../../service/Api";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface Testimonial {
  id: number;
  author_name: string;
  author_description: string;
  company_name: string | null;
  rating: number;
  content: string;
  display_order: number;
}

interface TestimonialsSelectorProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

const TestimonialsSelector: React.FC<TestimonialsSelectorProps> = ({
  selectedIds,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [tempSelectedIds, setTempSelectedIds] = useState<number[]>(selectedIds);
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiService.get<Testimonial[]>(
          "/api/configurations/testimonials/",
        );
        setTestimonials(response.data || []);
      } catch (error) {
        console.error("Failed to fetch testimonials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Sync temp selection when modal opens
  useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedIds);
    }
  }, [open, selectedIds]);

  const handleToggle = (id: number) => {
    const currentIndex = tempSelectedIds.indexOf(id);
    let newSelectedIds: number[] = [];

    if (currentIndex === -1) {
      // Add if less than 3 selected
      if (tempSelectedIds.length < 3) {
        newSelectedIds = [...tempSelectedIds, id];
      } else {
        return;
      }
    } else {
      // Remove from selection
      newSelectedIds = tempSelectedIds.filter((selectedId) => selectedId !== id);
    }

    setTempSelectedIds(newSelectedIds);
  };

  const handleSave = () => {
    onChange(tempSelectedIds);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedIds(selectedIds);
    setOpen(false);
  };

  const isSelected = (id: number) => tempSelectedIds.indexOf(id) !== -1;
  const isDisabled = (id: number) =>
    tempSelectedIds.length >= 3 && !isSelected(id);

  // Get selected testimonial names for the trigger button
  const getSelectedNames = () => {
    if (selectedIds.length === 0) return "None selected";
    const names = testimonials
      .filter((t) => selectedIds.includes(t.id))
      .map((t) => t.author_name);
    return names.join(", ");
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Testimonials
        </Typography>
        {/* <Chip
          label={`${selectedIds.length}/3 selected`}
          size="small"
          sx={{
            bgcolor:
              selectedIds.length === 3
                ? colors.primary[400]
                : "#e0e0e0",
            color: selectedIds.length === 3 ? "white" : "text.secondary",
            fontWeight: 600,
          }}
        /> */}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {getSelectedNames()}
      </Typography>

      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        fullWidth
        sx={{
          borderColor: colors.primary[400],
          color: colors.primary[400],
          "&:hover": {
            borderColor: colors.primary[500],
            bgcolor: "rgba(0,0,0,0.02)",
          },
        }}
      >
        Select Testimonials
      </Button>

      {/* Modal */}
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6">Select Testimonials</Typography>
              <Typography variant="caption" color="text.secondary">
                Choose up to 3 testimonials to display in your proposal
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Chip
                label={`${tempSelectedIds.length}/3 selected`}
                size="small"
                sx={{
                  bgcolor:
                    tempSelectedIds.length === 3
                      ? colors.primary[400]
                      : "#e0e0e0",
                  color:
                    tempSelectedIds.length === 3 ? "white" : "text.secondary",
                  fontWeight: 600,
                }}
              />
              <IconButton onClick={handleCancel}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading testimonials...
              </Typography>
            </Box>
          ) : testimonials.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: "center", py: 8 }}>
              No testimonials available.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 500, overflowY: "auto", pr: 1 }}>
              {testimonials.map((testimonial) => (
                <Paper
                  key={testimonial.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: 1,
                    borderColor: isSelected(testimonial.id)
                      ? colors.primary[400]
                      : "divider",
                    borderWidth: isSelected(testimonial.id) ? 2 : 1,
                    bgcolor: isDisabled(testimonial.id) ? colors.primary[800] : colors.primary[900],
                    cursor: isDisabled(testimonial.id)
                      ? "not-allowed"
                      : "pointer",
                    opacity: isDisabled(testimonial.id) ? 0.6 : 1,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: isDisabled(testimonial.id)
                        ? colors.primary[800]
                        : "action.hover",
                    },
                  }}
                  onClick={() =>
                    !isDisabled(testimonial.id) && handleToggle(testimonial.id)
                  }
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Checkbox
                      checked={isSelected(testimonial.id)}
                      disabled={isDisabled(testimonial.id)}
                      onChange={() => handleToggle(testimonial.id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: colors.primary[400],
                        "&.Mui-checked": {
                          color: colors.primary[400],
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          lineHeight: 1.6,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {`"${testimonial.content}"`}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        — {testimonial.author_name}
                        {testimonial.company_name &&
                          `, ${testimonial.company_name}`}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ bgcolor: colors.primary[400] }}
          >
            Save Selection
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestimonialsSelector;